/**
 * @carrier/ocs-client — OCS API client (Workers-compatible).
 *
 * Shared between apps/mcp-server and apps/api.
 * All requests POST to `${baseUrl}/v1?token=<api_key>` with body `{ methodName: params }`.
 * Responses: `{ status: { code, msg }, methodName: { ...data } }`.
 *
 * Money-leak guardrails (NEVER LEAK MONEY):
 *  - Per-second floor: ≤10 calls/sec/reseller (see rate-floor.ts).
 *  - Per-endpoint per-minute governor: enforces Bridge4IP limits per endpoint
 *    (30–600/min depending on method) with 80/20 interactive/batch split (see rate-governor.ts).
 *    On 429 / OCS error code 100 (TRAFFIC_CONTROL_LIMIT_EXCEEDED): exponential backoff + log.
 *  - Specific error codes 12, 17, 100, 10001-10004 carry structured messages.
 */

import { acquireToken } from "./rate-floor.js";
import { acquireEndpointSlot, type CallPriority } from "./rate-governor.js";

// The per-second floor's buckets are module state. Tests that assert rate-floor
// behaviour have to clear them between cases, and they now do that against THIS
// client rather than an app-local copy of it — so the helper has to be reachable
// from the package's public surface. `rate-floor.ts` itself stays unexported:
// `acquireToken` is an internal chokepoint and nothing outside should call it.
export { _resetBucketsForTest } from "./rate-floor.js";
import { OCS_V1_METHODS, RESPONSE_SCHEMAS } from "./schemas.js";
import { assertBareIntParam, applyParamRenames } from "./ocs-param-shapes.js";
import { OcsTimeoutError, runWithBudget } from "./timeout-budgets.js";

export interface OcsStatus {
  code: number;
  msg: string;
}

export interface OcsResponse<T = Record<string, unknown>> {
  status: OcsStatus;
  [method: string]: T | OcsStatus;
}

export interface OcsApiErrorDetails {
  /** For create_location_zone validation errors (codes 10001-10004). */
  invalidTadigs?: string[];
  existingLZ?: unknown;
  [key: string]: unknown;
}

export class OcsApiError extends Error {
  public readonly details: OcsApiErrorDetails;

  constructor(
    public readonly code: number,
    message: string,
    public readonly method: string,
    details: OcsApiErrorDetails = {},
  ) {
    super(`[${method}] OCS error ${code}: ${message}`);
    this.name = "OcsApiError";
    this.details = details;
  }
}

/**
 * Thrown when `call()` is invoked with a method that is NOT one of the
 * known-valid OCS v1 methods (see `OCS_V1_METHODS`). Catches phantom methods
 * like `createAccount` / `createSubscriber` at dev time — these are not OCS
 * methods and the server silently returns a 200 + error-2 envelope for them.
 * Only thrown in strict mode (`{ strictMethods: true }`); otherwise `call()`
 * warns once and proceeds.
 */
export class OcsUnknownMethodError extends Error {
  constructor(public readonly method: string) {
    super(
      `[${method}] is not a known OCS v1 method. ` +
        `It is not in the verified set of ${OCS_V1_METHODS.size} methods ` +
        `(createAccount/createSubscriber do not exist on the OCS). ` +
        `If this is a genuinely new method, add it to OCS_V1_METHODS or ` +
        `construct the client without { strictMethods: true }.`,
    );
    this.name = "OcsUnknownMethodError";
  }
}

/**
 * Thrown when a response payload that has a pinned schema in
 * `RESPONSE_SCHEMAS` fails shape validation — i.e. the OCS returned a 200 with
 * status.code 0 but a body shaped differently than the contract expects. This
 * surfaces provider drift loudly instead of returning a mis-shaped object that
 * blows up far away from the boundary. Skip per-client with
 * `{ validateResponses: false }` for forward-compat.
 */
/** A single contract-validation failure: where it failed and why. */
export interface OcsContractIssue {
  /** Dotted path into the payload (e.g. `reseller.0.account`); `<root>` for top-level. */
  path: string;
  /** Human-readable reason from the underlying Zod issue. */
  message: string;
}

export class OcsContractError extends Error {
  constructor(
    public readonly method: string,
    /** Structured list of what failed — inspect programmatically, don't parse the message. */
    public readonly issues: OcsContractIssue[],
    public readonly payload: unknown,
  ) {
    super(
      `[${method}] OCS response failed contract validation: ${OcsContractError.summarize(issues)}`,
    );
    this.name = "OcsContractError";
  }

  /** One-line, human-readable join of the issues — for logs and the error message. */
  get summary(): string {
    return OcsContractError.summarize(this.issues);
  }

  private static summarize(issues: OcsContractIssue[]): string {
    return issues.map((i) => `${i.path}: ${i.message}`).join("; ");
  }
}

// ---------------------------------------------------------------------------
// Error code message routing
// ---------------------------------------------------------------------------

function ocsErrorMessage(
  code: number,
  rawMsg: string,
  rawBody: Record<string, unknown>,
): { message: string; details: OcsApiErrorDetails } {
  switch (code) {
    case 12:
      return {
        message:
          "OCS resource is read-only (code 12). The package/subscriber/account state " +
          "does not allow this mutation. Often returned for end-of-life subscribers or " +
          "finalized invoices.",
        details: {},
      };
    case 17:
      return {
        message:
          "Subscriber is end-of-life (code 17). It can no longer be mutated. " +
          "Use modify_subscriber_status to revive before further changes.",
        details: {},
      };
    default:
      if (code >= 10001 && code <= 10004) {
        // create_location_zone validation — preserve invalidTadigs / existingLZ
        const details: OcsApiErrorDetails = {};
        if (rawBody["invalidTadigs"] !== undefined) {
          details.invalidTadigs = rawBody["invalidTadigs"] as string[];
        }
        if (rawBody["existingLZ"] !== undefined) {
          details.existingLZ = rawBody["existingLZ"];
        }
        return { message: rawMsg || "Location zone validation failed", details };
      }
      return { message: rawMsg || "Unknown error", details: {} };
  }
}

// ---------------------------------------------------------------------------
// Retry with exponential backoff + jitter
// ---------------------------------------------------------------------------

const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 4_000;
const BACKOFF_MAX_RETRIES = 3;
// OCS code 100 is a provider traffic-control response, not an application error.
// Its enforcement window can outlast the normal network-error retry cadence.
const TRAFFIC_CONTROL_BACKOFF_BASE_MS = 1_000;
const TRAFFIC_CONTROL_BACKOFF_MAX_MS = 16_000;
const TRAFFIC_CONTROL_MAX_RETRIES = 6;
const BACKOFF_JITTER = 0.25; // ±25%
/**
 * Exported so callers (e.g. intelligence.ts) can wrap per-iteration OCS calls.
 * Network failures get 3 retries (500ms exponential, capped at 4s). OCS code 100
 * gets 6 retries (1s exponential, capped at 16s) because provider traffic-control
 * windows routinely outlast a short network retry burst.
 */
export async function retryWithBackoff<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  let maxRetries = BACKOFF_MAX_RETRIES;
  let backoffBaseMs = BACKOFF_BASE_MS;
  let backoffMaxMs = BACKOFF_MAX_MS;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      const base = Math.min(backoffBaseMs * Math.pow(2, attempt - 1), backoffMaxMs);
      const jitter = base * BACKOFF_JITTER * (2 * Math.random() - 1);
      const delay = Math.max(0, Math.round(base + jitter));
      await new Promise<void>((r) => setTimeout(r, delay));
    }
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (err instanceof OcsApiError) {
        // Code 100 is a transient provider traffic-control response. It needs a
        // longer retry window than network failures; preserve all other OCS errors.
        if (err.code !== 100) throw err;
        maxRetries = TRAFFIC_CONTROL_MAX_RETRIES;
        backoffBaseMs = TRAFFIC_CONTROL_BACKOFF_BASE_MS;
        backoffMaxMs = TRAFFIC_CONTROL_BACKOFF_MAX_MS;
      }
      // Contract failures are deterministic — don't waste retries on them.
      if (err instanceof OcsContractError) {
        throw err;
      }
      // Timeout retries belong to runWithBudget, which is the only layer that
      // knows the total budget. Retrying here as well would multiply the
      // ceiling the caller was promised by four.
      if (err instanceof OcsTimeoutError) {
        throw err;
      }
    }
  }
  throw lastError;
}

/**
 * True when an error is an abort raised by our own deadline signal.
 *
 * PORTED FROM apps/mcp-server/src/client.ts ON 2026-08-28, AND THAT IS THE POINT.
 *   The #663 request-budget work landed on the Worker's private copy of this
 *   client and never reached this package or the stdio copy. The consequence was
 *   not cosmetic: without this mapping an abort raised by our OWN deadline comes
 *   out of `fetch` as a plain `Error`, which `retryWithBackoff` classifies as a
 *   retriable network error and runs three more times — multiplying the total
 *   wall-clock ceiling the caller was promised. `runWithBudget` is the only layer
 *   that knows the total budget, so the timeout has to surface as the
 *   non-retriable `OcsTimeoutError` for its accounting to hold.
 *
 *   The Worker's own tests (apps/mcp-server/tests/ocs-budgets.test.ts) caught
 *   this the moment the Worker was pointed at the shared client — which is the
 *   whole argument for having one implementation: the gap existed for as long as
 *   the copies did, and closing the fork is what surfaced it.
 */
function isAbortError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err.name === "AbortError" ||
      err.name === "TimeoutError" ||
      /aborted|timed? ?out/i.test(err.message))
  );
}

// ---------------------------------------------------------------------------
// OCS Client
// ---------------------------------------------------------------------------

export interface OcsClientOptions {
  /**
   * Enforce the known-method guard: when true, calling a method not in
   * `OCS_V1_METHODS` throws `OcsUnknownMethodError`. Default **false**
   * (permissive) — the repo currently calls a number of method names that
   * aren't in the live-verified OCS v1 set (a separate reconciliation effort),
   * so enforcing here would break existing call sites. Opt in (`true`) for
   * dev/tests or once the codebase's method vocabulary is reconciled. An
   * unrecognized method is warned once regardless, so the signal isn't lost.
   */
  strictMethods?: boolean;
  /**
   * Run response-shape validation against `RESPONSE_SCHEMAS`. Default true. A
   * mismatch WARNS once (does not throw) unless `strictValidation` is set. Set
   * false to skip validation entirely (e.g. consumers that do their own
   * shape-tolerant parsing).
   */
  validateResponses?: boolean;
  /**
   * Throw `OcsContractError` on a response-shape mismatch instead of warning.
   * Default false. Enable in dev/CI/tests to fail loudly on provider drift;
   * left off in prod so an un-sampled shape variant can't break live traffic.
   */
  strictValidation?: boolean;
}

export class OcsClient {
  private readonly baseUrl: string;
  private readonly _strictMethods: boolean;
  private readonly _validateResponses: boolean;
  private readonly _strictValidation: boolean;
  // Per-instance (NOT static): a static set would dedup across requests in a
  // reused Worker isolate and silently swallow recurring contract-drift warnings.
  private readonly _warnedMethods = new Set<string>();
  private readonly _warnedValidation = new Set<string>();

  /**
   * @param baseUrl        OCS base URL without trailing slash (e.g. https://ocs.esimvault.cloud).
   * @param _defaultToken  Default API token; identifies the reseller for rate-floor keying.
   * @param _priority      Call priority: "interactive" (MCP tool calls, 80% capacity) or
   *                       "batch" (cron/poll callers, 20% capacity). Default: "interactive".
   * @param options        Contract-enforcement opt-outs (see `OcsClientOptions`).
   */
  constructor(
    baseUrl: string,
    private readonly _defaultToken?: string,
    private readonly _priority: CallPriority = "interactive",
    options: OcsClientOptions = {},
  ) {
    // Trimmed by index, not by `/\/+$/`. That regex backtracks polynomially on
    // a long run of slashes (CodeQL js/polynomial-redos), and this constructor
    // is now reachable from `@carrier/onboarding` with a base URL that comes
    // from Worker config — still trusted, but the cheap fix is cheaper than
    // the argument.
    let end = baseUrl.length;
    while (end > 0 && baseUrl.charCodeAt(end - 1) === 47 /* "/" */) end--;
    this.baseUrl = baseUrl.slice(0, end);
    this._strictMethods = options.strictMethods ?? false;
    this._validateResponses = options.validateResponses ?? true;
    this._strictValidation = options.strictValidation ?? false;
  }

  async call<T = Record<string, unknown>>(
    method: string,
    params: Record<string, unknown> | number | string = {},
    token?: string,
  ): Promise<T> {
    // Method guard. The live-verified OCS v1 set (OCS_V1_METHODS) does not (yet)
    // cover every name the repo calls, so by default we WARN once rather than
    // throw — surfacing phantom methods (createAccount/createSubscriber, etc.)
    // without breaking existing call sites. Strict mode (opt-in) throws.
    if (!OCS_V1_METHODS.has(method)) {
      if (this._strictMethods) throw new OcsUnknownMethodError(method);
      if (!this._warnedMethods.has(method)) {
        this._warnedMethods.add(method);
        console.warn(
          `[ocs-client] method "${method}" is not in the verified OCS v1 set — ` +
            `it may not exist on this OCS instance (see OCS_V1_METHODS).`,
        );
      }
    }

    const tok = token ?? this._defaultToken;
    if (!tok) {
      throw new OcsApiError(-1, "No OCS API token provided", method);
    }

    const url = `${this.baseUrl}/v1?token=${tok}`;
    // Shape gate. Some OCS methods take a bare integer id; sending an object
    // makes OCS reject the entire request with an opaque Jackson deserialiser
    // error that the UI renders as an empty panel. Fail here — at the one place
    // every surface serialises through — with a message that names the mistake.
    assertBareIntParam(method, params);
    // Field-name gate, same chokepoint and same reason. OCS rejects the whole
    // request on an unknown key, so a tool advertising `resellerId` where OCS
    // wants `id` errors mid-loop and the model gives up or invents an answer.
    // Translating here keeps the published tool schema stable across every
    // surface — MCP server, stdio, API, CLI — and future call sites.
    const wireParams = applyParamRenames(method, params);
    const body = JSON.stringify({ [method]: wireParams });

    return retryWithBackoff(async () => {
      // Layer 1 — Per-second floor: ≤10 calls/sec keyed by token.
      await acquireToken(tok);

      // Layer 2 — Per-endpoint per-minute governor (each retryable attempt consumes one slot).
      await acquireEndpointSlot(tok, method, this._priority);

      // Layer 3 — Request budget: per-method deadline, retry-on-timeout, and a
      // total wall-clock ceiling. See timeout-budgets.ts for the measurements.
      return runWithBudget<T>(method, async (signal, attemptMs) => {
        let res: Response;
        try {
          res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            signal,
          });
        } catch (err) {
          if (isAbortError(err)) {
            // Our own deadline fired at the socket level — surface it as the
            // non-retriable timeout rather than as a retriable network error.
            throw new OcsTimeoutError(method, attemptMs);
          }
          // Network error — retryWithBackoff will retry on non-OcsApiError
          throw err;
        }

        if (res.status >= 500) {
          // Treat 5xx as retryable network-layer errors (not OcsApiError, so backoff retries)
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }

        if (!res.ok) {
          throw new OcsApiError(
            res.status,
            `HTTP ${res.status} ${res.statusText}`,
            method,
          );
        }

        // The body read is inside the deadline too: #663's hang was a response
        // that started but never completed, which a headers-only timeout misses.
        let json: OcsResponse<T>;
        try {
          json = (await res.json()) as OcsResponse<T>;
        } catch (err) {
          if (isAbortError(err)) {
            throw new OcsTimeoutError(method, attemptMs);
          }
          throw err;
        }

        if (json.status?.code !== 0) {
          const ocsCode = json.status?.code ?? -1;
          const rawMsg = json.status?.msg ?? "Unknown error";
          // Pass full json body for error-detail extraction (e.g. invalidTadigs)
          const rawBody = json as unknown as Record<string, unknown>;
          const { message, details } = ocsErrorMessage(ocsCode, rawMsg, rawBody);
          throw new OcsApiError(ocsCode, message, method, details);
        }

        // Resolve the response payload, honoring the live-OCS key remaps.
        let payload: T;
        if (method === "getCustomerTariff" && json["listTariffRule"] !== undefined) {
          // Fix #14: getCustomerTariff response keyed as "listTariffRule"
          payload = json["listTariffRule"] as T;
        } else if (method === "getSubscriberLocationByCellId") {
          const byMethod = json[method] as T | undefined;
          if (byMethod !== undefined) {
            // Present null is a valid payload; do not fall through to envelope fallback.
            payload = byMethod;
          } else if (json["subscriberLocation"] !== undefined) {
            // GeoSense: live OCS returns coordinates under "subscriberLocation", not the method name
            payload = json["subscriberLocation"] as T;
          } else {
            payload = (json[method] as T) ?? (json as unknown as T);
          }
        } else if (json[method] !== undefined) {
          payload = json[method] as T;
        } else if (Object.keys(json).some((k) => k !== "status")) {
          // OCS keyed the response under something other than the method name
          // (e.g. getSingleSubscriber -> {status, subscriber}). Callers that
          // read a differently-named top-level key off the raw envelope (see
          // ocs-scoping.ts's subscriberAccountId) depend on this fallback —
          // narrow it to "envelope carries more than just status" so it does
          // not also catch the success-with-nothing-but-status case below.
          payload = json as unknown as T;
        } else {
          // OCS answered status.code 0 (success) and the envelope carries
          // NOTHING beyond that status — observed live for getSimProviderStatus
          // (`{status:{code:0,msg:"OK"}}` and nothing else). The old fallback
          // `json as unknown as T` handed the caller that bare envelope, so
          // `payload.status` (an `{code,msg}` object) got read as if it were
          // the method's actual field of the same name — e.g.
          // diagnose_subscriber's `String(sim.data.status)` on that object
          // produced the literal string "[object Object]" and was reported as
          // a critical finding. A success envelope with nothing else means OCS
          // has no data to report for this call; treat it as an absent
          // payload so every downstream `if (data)` / `data?.field` guard
          // degrades safely instead of reading envelope plumbing as data.
          payload = null as T;
        }

        // Contract validation: if this method has a pinned response schema, assert
        // the payload shape. A mismatch = provider drift. By default we WARN once
        // (surfacing the drift without breaking the call — the live shapes are a
        // sampled snapshot, so we don't hard-fail prod traffic on an un-sampled
        // variant). Opt into `{ strictValidation: true }` to throw OcsContractError.
        if (this._validateResponses) {
          const schema = RESPONSE_SCHEMAS[method];
          if (schema) {
            const parsed = schema.safeParse(payload);
            if (!parsed.success) {
              const issues: OcsContractIssue[] = parsed.error.issues.map((i) => ({
                path: i.path.join(".") || "<root>",
                message: i.message,
              }));
              if (this._strictValidation) {
                throw new OcsContractError(method, issues, payload);
              }
              if (!this._warnedValidation.has(method)) {
                this._warnedValidation.add(method);
                console.warn(`[ocs-client] ${new OcsContractError(method, issues, payload).message}`);
              }
            }
          }
        }

        return payload;
      });
    });
  }
}
